import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { CheckCircle2, Loader2 } from "lucide-react";
import { PAYMENT } from "@/constants/testIds";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("checking");
  const [amount, setAmount] = useState(null);
  const { clear } = useCart();
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    if (!sessionId) return;
    let cancelled = false;

    const poll = async (attempt = 0) => {
      if (cancelled) return;
      if (attempt > 15) {
        setStatus("timeout");
        return;
      }
      setAttempts(attempt);
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setStatus("paid");
          setAmount(data.amount);
          clear();
          return;
        }
        if (data.payment_status === "failed" || data.payment_status === "expired") {
          setStatus("failed");
          return;
        }
        setTimeout(() => poll(attempt + 1), 2000);
      } catch {
        setTimeout(() => poll(attempt + 1), 2500);
      }
    };
    poll(0);
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line
  }, [sessionId]);

  return (
    <div className="pt-32 pb-32 min-h-screen flex items-center">
      <div className="max-w-2xl mx-auto px-6 text-center">
        {status === "checking" && (
          <>
            <Loader2 strokeWidth={1.5} className="w-14 h-14 mx-auto text-stone-500 animate-spin" />
            <h1 className="mt-8 font-serif text-4xl md:text-5xl font-light tracking-tight text-[#1A1C1A]">
              Confirming your order…
            </h1>
            <p className="mt-4 text-stone-600">Attempt {attempts + 1}. This takes a moment.</p>
          </>
        )}

        {status === "paid" && (
          <>
            <CheckCircle2 strokeWidth={1.2} className="w-16 h-16 mx-auto text-[#4A6741]" />
            <p className="mt-8 text-xs uppercase tracking-[0.3em] text-stone-500 font-semibold">Order confirmed</p>
            <h1 data-testid={PAYMENT.successHeading} className="mt-4 font-serif text-5xl md:text-6xl font-light tracking-tighter text-[#1A1C1A]">
              Thank you. A <em className="italic text-stone-600">new beginning</em>.
            </h1>
            {amount != null && (
              <p className="mt-6 font-sans text-lg text-stone-700">
                Total charged: <span className="text-[#1A1C1A]">${(amount / 100).toFixed(2)}</span>
              </p>
            )}
            <p className="mt-6 text-stone-600 max-w-md mx-auto font-light">
              You'll receive an email receipt from Stripe with your order details. We'll ship your pieces with
              intention and a blessing.
            </p>
            <Link
              to="/shop"
              data-testid={PAYMENT.continueShoppingButton}
              className="mt-10 inline-block bg-[#1A1C1A] text-[#F9F9F6] hover:bg-[#3C3E3C] px-10 py-4 rounded-full font-sans text-sm tracking-wider uppercase"
            >
              Continue Shopping
            </Link>
          </>
        )}

        {status === "failed" && (
          <>
            <h1 className="font-serif text-5xl italic text-stone-600">Payment didn't complete.</h1>
            <Link to="/cart" className="mt-8 inline-block underline">Return to cart</Link>
          </>
        )}

        {status === "timeout" && (
          <>
            <h1 className="font-serif text-5xl italic text-stone-600">Still processing…</h1>
            <p className="mt-4 text-stone-600">Your order is being confirmed. Check your email shortly.</p>
            <Link to="/" className="mt-8 inline-block underline">Return home</Link>
          </>
        )}
      </div>
    </div>
  );
}
